'use client';
import React from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { getStoredToken, clearToken, authHeaders } from '@/lib/auth';

export default function Shell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [compact, setCompact] = React.useState<boolean>(false);
  const [authState, setAuthState] = React.useState<'pending' | 'ok'>('pending');

  React.useEffect(() => {
    const v = localStorage.getItem('portal_sidebar_compact');
    if (v) setCompact(v === '1');
  }, []);

  // Bearer-token gate. Validates the stored basbot_token against `/api/me`
  // (which nginx proxies to basbot). Login page is excluded so the user
  // can paste a token without being bounced.
  React.useEffect(() => {
    if (pathname?.startsWith('/login')) {
      setAuthState('ok');
      return;
    }
    const token = getStoredToken();
    if (!token) {
      const returnUrl = encodeURIComponent(pathname || '/');
      router.replace(`/login?returnUrl=${returnUrl}`);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/api/me', { headers: authHeaders() });
        if (!alive) return;
        if (res.ok) {
          setAuthState('ok');
        } else if (res.status === 401 || res.status === 403) {
          clearToken();
          const returnUrl = encodeURIComponent(pathname || '/');
          router.replace(`/login?returnUrl=${returnUrl}`);
        } else {
          // Server hiccup — show the UI rather than bouncing.
          setAuthState('ok');
        }
      } catch {
        if (!alive) return;
        // Network blip; render UI in a degraded state instead of redirecting.
        setAuthState('ok');
      }
    })();
    return () => { alive = false; };
  }, [pathname, router]);

  const toggle = () => {
    setCompact((prev) => {
      const n = !prev;
      localStorage.setItem('portal_sidebar_compact', n ? '1' : '0');
      return n;
    });
  };

  // Login page renders bare — no header, no sidebar.
  if (pathname?.startsWith('/login')) {
    return <>{children}</>;
  }

  if (authState === 'pending') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f6f1]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#111827]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f6f1] text-gray-900">
      <Header onToggleSidebar={toggle} />
      <div className="flex" style={{ paddingTop: 'var(--header-h)', minHeight: 'calc(100vh - var(--header-h))' }}>
        <div className="flex-shrink-0">
          <div className="sticky top-0" style={{ height: 'calc(100vh - var(--header-h))' }}>
            <Sidebar compact={compact} />
          </div>
        </div>
        <div className="flex-1 min-w-0 pl-6">
          {children}
        </div>
      </div>
    </div>
  );
}
